import React, { useEffect, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import { IntroducingBrokerDetailsDto } from '@/api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import { MultiSelect } from '@/components/Select'
import { SelectOptionInput } from '@/components/Select/Select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Body1, Heading5 } from '@/components/ui/typography'
import { useApiClient } from '@/utils/api/ApiClient'
import { PartnershipsApiClient } from '@/utils/api/partnershipsApi'
import { SearchApiClient } from '@/utils/api/searchApi'
import { SearchResultDto } from 'api/Search/Types/SearchResultDto'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet'
import { Form, FormikProps } from 'formik'
import { Calendar, Filter, MapPin, Users } from 'lucide-react'
import { useAppSelector } from 'store'

export interface DashboardFormProps {
  brokers: string[]
  dateRange: [Date | null, Date | null]
  country: string[]
}

interface DashboardFormComponentProps extends FormikProps<DashboardFormProps> {
  isOpen: boolean
  onClose: () => void
  lastUpdated: Date | null
  onResetFilters: () => void
}

const DashboardForm: React.FC<DashboardFormComponentProps> = ({
  values,
  setFieldValue,
  isOpen,
  onClose,
  lastUpdated,
  resetForm,
  onResetFilters,
}) => {
  const allCountries = useAppSelector((state) => state.identity.countries) || []

  const searchApiClient = useApiClient(SearchApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const [activeDateFilter, setActiveDateFilter] = useState<string | null>(null)
  const [brokerOptions, setBrokerOptions] = useState<SelectOptionInput[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [ibCountries, setIbCountries] = useState<string[]>([])
  const [isLoadingBrokers, setIsLoadingBrokers] = useState(false)
  const [selectedBrokerOptions, setSelectedBrokerOptions] = useState<SelectOptionInput[]>([])

  const mergedBrokerOptions = useMemo(() => {
    const optionsByValue = new Map<string, SelectOptionInput>()

    selectedBrokerOptions.forEach((option) => {
      optionsByValue.set(String(option.value), option)
    })

    brokerOptions.forEach((option) => {
      optionsByValue.set(String(option.value), option)
    })

    return Array.from(optionsByValue.values())
  }, [brokerOptions, selectedBrokerOptions])

  const searchBrokers = async (query: string) => {
    try {
      setIsLoadingBrokers(true)

      const response = await searchApiClient.getSearchResults({
        text: query,
        type: 'introducing_broker',
        pageSize: 20,
        pageIndex: 1,
      })

      setBrokerOptions(
        (response.items ?? []).map((broker: SearchResultDto) => ({
          value: broker.id,
          label: broker.value || broker.id,
        }))
      )
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoadingBrokers(false)
    }
  }

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setBrokerOptions([])
      return
    }

    const timeout = setTimeout(() => {
      searchBrokers(searchQuery)
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchQuery])

  useEffect(() => {
    const loadSelectedBrokers = async () => {
      if (!values.brokers.length) {
        setSelectedBrokerOptions([])
        return
      }

      try {
        const responses: IntroducingBrokerDetailsDto[] = await Promise.all(
          values.brokers.map((id: string) => partnershipsApiClient.getIntroducingBrokerById(id))
        )

        const mapped = responses.map((ib) => ({
          value: ib.id,
          label: ib.name ?? ib.id,
        }))

        setSelectedBrokerOptions(mapped)
      } catch (e) {
        console.error(e)
      }
    }

    void loadSelectedBrokers()
  }, [values.brokers])

  useEffect(() => {
    if (values.brokers.length === 0) {
      setSelectedBrokerOptions([])
    }
  }, [values.brokers])

  useEffect(() => {
    const loadIbCountries = async () => {
      if (!values.brokers.length) {
        setIbCountries([])
        return
      }

      try {
        const responses = await Promise.all(
          values.brokers.map((id: string) => partnershipsApiClient.getIntroducingBrokerById(id))
        )

        const countriesFromIb = responses
          .map((ib) => ib.client.country?.id)
          .filter((id): id is string => Boolean(id))

        setIbCountries(countriesFromIb)
      } catch (e) {
        console.error(e)
      }
    }

    loadIbCountries()
  }, [values.brokers])

  const countryOptions = allCountries
    .filter((c) => (ibCountries.length ? ibCountries.includes(c.id) : true))
    .map((c) => ({
      value: c.id,
      label: c.name,
    }))

  return (
    <Sheet open={isOpen} onOpenChange={onClose} modal={false}>
      <SheetContent className='w-[450px] overflow-y-auto sm:w-[600px]'>
        <SheetHeader>
          <SheetTitle className='flex items-center gap-2 text-2xl'>
            <Filter className='h-6 w-6 text-purple-600' />
            Dashboard Filters
          </SheetTitle>
          <SheetDescription>
            {lastUpdated ? (
              <Heading5>Last updated: {lastUpdated.toLocaleString()}</Heading5>
            ) : (
              <Heading5>Configure your dashboard filters</Heading5>
            )}
          </SheetDescription>
        </SheetHeader>

        <Form className='p-4 space-y-4'>
          <div className='space-y-2'>
            <Body1 className='flex items-center gap-2 text-sm font-semibold'>
              <Users className='h-4 w-4 text-purple-600' />
              Introducing Brokers
            </Body1>

            <MultiSelect
              className='flex-1'
              placeholder='Search brokers'
              options={mergedBrokerOptions}
              size='lg'
              value={values.brokers}
              showLoading={isLoadingBrokers}
              name='brokers'
              onChange={(v) => setFieldValue('brokers', v)}
              onSearch={setSearchQuery}
              allowClear
              selectionSummaryThreshold={5}
              showSearch
              notFoundContent={
                searchQuery.length > 0 && searchQuery.length < 2
                  ? 'Please enter at least 2 characters'
                  : 'No brokers found'
              }
            />
          </div>

          <div className='space-y-2'>
            <Body1 className='flex items-center gap-2 text-sm font-semibold '>
              <Calendar className='h-4 w-4 text-purple-600' />
              Date Range
            </Body1>

            <div className='space-y-2'>
              <ToggleGroup
                className='w-full'
                variant='outline'
                defaultValue={[activeDateFilter || '']}
              >
                {[
                  { label: '1M', months: 1 },
                  { label: '3M', months: 3 },
                  { label: '6M', months: 6 },
                  { label: '12M', months: 12 },
                ].map(({ label, months }) => (
                  <ToggleGroupItem
                    className='flex-1'
                    value={months.toString()}
                    onClick={() => {
                      const end = new Date()
                      const start = new Date()
                      start.setMonth(start.getMonth() - months)
                      setFieldValue('dateRange', [start, end])
                      setActiveDateFilter(months.toString())
                    }}
                    aria-label={label}
                  >
                    {label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>

              <DatePicker
                selectsRange
                dropdownMode='select'
                startDate={values.dateRange?.[0]}
                endDate={values.dateRange?.[1]}
                onChange={(update) => setFieldValue('dateRange', update)}
                popperClassName='z-[9999]'
                placeholderText='Select date range'
                wrapperClassName='w-full'
                isClearable
                monthsShown={2}
                customInput={<Input data-test={'dateRange'} id={'dateRange'} />}
              />
            </div>
          </div>

          <div className='space-y-2'>
            <Body1 className='flex items-center gap-2 text-sm font-semibold '>
              <MapPin className='h-4 w-4 text-purple-600' />
              Countries
            </Body1>
            <div className='flex gap-1 w-full'>
              <MultiSelect
                className='flex-1'
                placeholder='Select countries'
                options={countryOptions}
                size='lg'
                value={values.country}
                onChange={(v) => setFieldValue('country', v)}
                allowClear
                selectionSummaryThreshold={5}
                showSearch
              />
              <Button
                onClick={() =>
                  setFieldValue(
                    'country',
                    allCountries.map((c) => c.id)
                  )
                }
              >
                Select All
              </Button>
            </div>
          </div>

          <SheetFooter className='mt-8 flex gap-2 flex-row w-full border-t border-gray-200 pt-6'>
            <Button
              onClick={() => {
                resetForm()
                onResetFilters()
                setSelectedBrokerOptions([])
                setSearchQuery('')
                setBrokerOptions([])
              }}
              className='flex-1'
              variant='destructive'
            >
              Reset
            </Button>
            <Button type='submit' className='flex-1'>
              Apply Filters
            </Button>
          </SheetFooter>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

export default DashboardForm
